import {
  type AuthorModel,
  type AuthorModelPagedResult,
  type GetApiAuthorsQueryParams,
  getApiAuthors,
} from '@/shared/api'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface AuthorsQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<GetApiAuthorsQueryParams>
  filtersInit: EventCallable<Partial<GetApiAuthorsQueryParams>>
  filtersChanged: EventCallable<Partial<GetApiAuthorsQueryParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Sort
  $sort: StoreWritable<AuthorsSortState>
  sortChanged: EventCallable<AuthorsSortState>
  sortReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiAuthorsQueryParams
  }>
  // Query
  query: Query<
    {
      params?: GetApiAuthorsQueryParams
    },
    AuthorModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
  // Accumulated
  $accumulatedData: StoreWritable<AuthorModel[]>
  accumulatedDataReset: EventCallable<void>
}

export interface AuthorsQueryModelWithPagination extends AuthorsQueryModelBase {
  $page: StoreWritable<number>
  pageIncreased: EventCallable<void>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface AuthorsQueryModelParams {
  initialFilters?: Partial<GetApiAuthorsQueryParams>
  usePagination: boolean
  accumulate: boolean
}

export interface AuthorsSortState {
  field: 'nickname' | null
  order: 'asc' | 'desc' | null
}

export function createAuthorsQueryModel(
  {
    initialFilters = {},
    usePagination = true,
    accumulate = false,
  }: AuthorsQueryModelParams = {
    initialFilters: {},
    usePagination: true,
    accumulate: false,
  },
): AuthorsQueryModelWithPagination | AuthorsQueryModelBase {
  // Query
  const query = createQuery({ handler: getApiAuthors })

  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<GetApiAuthorsQueryParams>>()
  const filtersChanged = createEvent<Partial<GetApiAuthorsQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiAuthorsQueryParams>(initialFilters)
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  //   Sort
  const initialSort: AuthorsSortState = { field: null, order: null }
  const sortChanged = createEvent<AuthorsSortState>()
  const sortReset = createEvent()

  const $sort = createStore<AuthorsSortState>(initialSort)
    .on(sortChanged, (_, payload) => payload)
    .on(sortReset, () => initialSort)

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageIncreased = createEvent()
  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .reset(pageReset)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    $sort,
    (filters, search, page, pageSize, sort) => {
      // Filters
      const params: GetApiAuthorsQueryParams = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Sort
      if (sort.field && sort.order) {
        if (sort.field === 'nickname') {
          if (sort.order === 'asc') params.SortByName = true
          else params.SortByNameDesc = true
        }
      }
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Accumulation
  const initialAccumulatedData: AuthorModel[] = []
  const accumulatedDataReset = createEvent()
  const $accumulatedData = createStore<AuthorModel[]>(initialAccumulatedData)
    .on(query.finished.success, (state, { result }) => {
      if (accumulate) {
        const map = new Map(state.map((item) => [item.id, item]))
        for (const item of result.items) {
          map.set(item.id, item)
        }
        return Array.from(map.values())
      }
      return result.items
    })
    .on(accumulatedDataReset, () => initialAccumulatedData)

  sample({
    clock: accumulatedDataReset,
    target: pageReset,
  })

  // Pagination
  sample({
    clock: pageIncreased,
    source: {
      page: $page,
      pageSize: $pageSize,
      total: query.$data.map((d) => d?.totalItems ?? 0),
      accumulated: $accumulatedData,
      pending: query.$pending,
    },
    filter: ({ page, pageSize, total, accumulated, pending }) => {
      const maxPage = Math.ceil(total / pageSize)
      const nextPage = page + 1
      const hasEnoughAccumulated = accumulated.length >= page * pageSize
      return !pending && nextPage <= maxPage && hasEnoughAccumulated
    },
    fn: ({ page }) => page + 1,
    target: pageChanged,
  })

  sample({
    clock: [$filters, $search, $sort],
    target: pageReset,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [
      filtersReset,
      searchReset,
      sortReset,
      pageReset,
      accumulatedDataReset,
    ],
  })

  const model: AuthorsQueryModelBase = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Search
    $search,
    searchChanged,
    searchReset,
    // Sort
    $sort,
    sortChanged,
    sortReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
    // Accumulated
    $accumulatedData,
    accumulatedDataReset,
  }

  const modelWithPagination: AuthorsQueryModelWithPagination = {
    ...model,
    $page,
    pageIncreased,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
